import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import { useBooking } from "@/hooks/useBooking";
import { useAuth } from "@/hooks/useAuth";
import { cityCenter } from "@/lib/locations";
import BookingSummaryCard from "./components/BookingSummaryCard";
import MessageThread from "./components/MessageThread";
import MeetupMap from "./components/MeetupMap";
import VideoCall from "./components/VideoCall";
import CarCheckin from "./components/CarCheckin";

export default function BookingPage() {
  const { id } = useParams();
  const bookingId = id ? Number(id) : null;
  const { booking, vehicle, loading, error, reload } = useBooking(bookingId);
  const { user, profile, loading: authLoading } = useAuth();

  const isRenter = !!user && !!booking && booking.renter_id === user.id;
  const isHost = !!user && !!booking && booking.owner_id === user.id;
  const isParticipant = isRenter || isHost;
  const viewer: "renter" | "host" = isHost && !isRenter ? "host" : "renter";

  if (loading || authLoading) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto w-full max-w-6xl px-4 pt-32 md:px-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]">
            <div className="h-80 w-full animate-pulse rounded-2xl bg-background-100" />
            <div className="h-80 w-full animate-pulse rounded-2xl bg-background-100" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 pt-40 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
            <i className="ri-chat-3-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
            {error || "This booking could not be found"}
          </h1>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={reload}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-900"
            >
              <i className="ri-refresh-line"></i> Try again
            </button>
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
            >
              <i className="ri-search-eye-line"></i> Browse cars
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!user || !isParticipant) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 pt-40 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
            <i className="ri-lock-2-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
            {user ? "This booking isn't yours" : "Sign in to view this booking"}
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            {user
              ? "Only the rider and host on a booking can open its pickup chat and live map."
              : "Sign in with the account you booked with to open the pickup chat and live meetup map."}
          </p>
          {!user && (
            <Link
              to={`/auth?redirect=/booking/${bookingId}`}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
            >
              <i className="ri-login-circle-line"></i> Sign in
            </Link>
          )}
        </div>
      </div>
    );
  }

  const pickup = {
    lat: Number(vehicle?.latitude ?? cityCenter(vehicle?.city).lat),
    lng: Number(vehicle?.longitude ?? cityCenter(vehicle?.city).lng),
  };
  const pickupLabel = booking.pickup_location || vehicle?.location || vehicle?.city || "Pickup point";
  const hostName = vehicle?.owner_name || "Your host";
  const riderName = booking.renter_name || profile?.full_name || "Gari rider";
  const otherParty = viewer === "host" ? riderName : hostName;
  const mapLive = booking.status === "confirmed" || booking.status === "ongoing";
  const selfName = profile?.full_name || user?.email || "You";
  const selfAvatar = profile?.avatar_url ?? null;
  const peerName = otherParty;
  const peerAvatar = viewer === "host" ? null : vehicle?.owner_avatar ?? null;
  const peerRole: "rider" | "host" = viewer === "host" ? "rider" : "host";

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 md:px-8 md:pt-36">
        <div className="max-w-2xl">
          <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">
            {viewer === "host" ? "Rider pickup" : "Your pickup"}
          </span>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            Meet &amp; greet
          </h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-600">
            Call, chat or film a walkaround with {otherParty}, and follow the live map so nobody gets lost.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]">
          <div className="space-y-6">
            <Reveal>
              <BookingSummaryCard booking={booking} vehicle={vehicle} viewer={viewer} />
            </Reveal>
            <Reveal delay={40}>
              <CarCheckin booking={booking} vehicle={vehicle} viewer={viewer} />
            </Reveal>
            <Reveal delay={80}>
              <MessageThread bookingId={booking.id} role={viewer} otherPartyName={otherParty} />
            </Reveal>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-28 lg:self-start">
            {mapLive ? (
              <MeetupMap
                pickup={pickup}
                pickupLabel={pickupLabel}
                hostName={hostName}
                riderName={riderName}
                live
                seed={booking.id}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-background-200 bg-background-100 p-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200/60 text-2xl text-foreground-500">
                  <i className="ri-road-map-line"></i>
                </span>
                <p className="mt-4 font-heading text-sm font-semibold text-foreground-950">Live map is warming up</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  {booking.status === "pending"
                    ? "Once the M-Pesa payment is confirmed, the live meetup map and driver tracking switch on."
                    : "This booking is no longer active, so live tracking is switched off."}
                </p>
              </div>
            )}

            <VideoCall
              selfName={selfName}
              selfAvatar={selfAvatar}
              peerName={peerName}
              peerAvatar={peerAvatar}
              peerRole={peerRole}
            />

            <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-information-line text-primary-500"></i> Pickup details
              </p>
              <ul className="mt-3 space-y-3 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-map-pin-line mt-0.5 text-foreground-500"></i>
                  <span>{pickupLabel}</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-calendar-line mt-0.5 text-foreground-500"></i>
                  <span>Pick up on {booking.start_date}</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-phone-line mt-0.5 text-foreground-500"></i>
                  <span>Share your number in the chat for a quick call</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}